import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, IndianRupee, TrendingUp, Zap, Megaphone, Scale, ShoppingCart, Factory, HeartPulse, GraduationCap, Building2, Landmark, Truck } from 'lucide-react';
import content from '../data/content.json';

const iconMap = {
  Clock: Clock,
  IndianRupee: IndianRupee,
  TrendingUp: TrendingUp,
  Zap: Zap,
  Megaphone: Megaphone,
  Scale: Scale,
  ShoppingCart: ShoppingCart,
  Factory: Factory,
  HeartPulse: HeartPulse,
  GraduationCap: GraduationCap,
  Building2: Building2,
  Landmark: Landmark,
  Truck: Truck
};

const Home = () => {
  const { hero, benefits, industriesWeServe, howItWorks, cta } = content.home;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(${hero.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        
        {/* Hero Content */}
        <div className="hero-content relative z-10">
          <h1 className="heading-1 mb-6">
            {hero.title}<br />
            <span className="text-blue">{hero.titleHighlight}</span>
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            {hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/free-audit" className="btn-orange">
              {hero.primaryCta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link to="/services" className="btn-secondary">
              {hero.secondaryCta}
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">{benefits.title}</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {benefits.subtitle}
            </p>
          </div>
          
          <div className="ai-grid">
            {benefits.items.map((benefit, index) => {
              const IconComponent = iconMap[benefit.icon];
              return (
                <div key={index} className="product-card text-center">
                  <div className="flex justify-center mb-4">
                    <IconComponent className="h-8 w-8 text-blue" />
                  </div>
                  <h3 className="heading-3 mb-3">{benefit.title}</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">{industriesWeServe.title}</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {industriesWeServe.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {industriesWeServe.items.map((industry, index) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <div key={index} className="product-card flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <IconComponent className="h-6 w-6" style={{ color: 'var(--accent-text)' }} />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>{industry.title}</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {industry.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-4">{howItWorks.title}</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {howItWorks.subtitle}
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              {howItWorks.steps.map((step, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="w-20 h-12 mx-auto mb-6 flex items-center justify-center text-white text-xl font-bold rounded-full"
                    style={{ background: 'var(--gradient-button)' }}
                  >
                    {step.number}
                  </div>
                  <h3 className="heading-3 mb-4">{step.title}</h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-2 mb-4">{cta.title}</h2>
            <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
              {cta.subtitle}
            </p>
            <Link to="/free-audit" className="btn-orange">
              {cta.buttonText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
